import { GetCurrentMember } from 'iam/SessionStore'
import { task } from 'mobx-task'
import BillingStore from 'billing/BillingStore'
import QS from 'qs'
import { observable, computed, runInAction, makeObservable } from 'mobx'
import Team from 'iam/models/Team'
import link from 'infra/link'
import { sessionStorage } from 'infra/storage'
import ModalViewModel from 'infra/ModalViewModel'
import PayoutMethodStore from 'payouts/pro/PayoutMethodStore'
import Member from 'iam/models/Member'

interface StripeCallbackParams {
  code: string
  scope?: string
  /**
   * Base64 encoded info
   */
  state?: string
}

interface StripeCallbackHandlerParams {
  code: string
}

export const StripeCallbackHandlerParamsStorageKey =
  'worklayer.stripeConnectHandlerParams'

export default class StripeCallbackViewModel {
  @observable redirectBackUrl: string | null = null

  constructor(private getCurrentMember: GetCurrentMember) {
    makeObservable(this)
  }

  activate = task(async (queryString: string) => {
    const query = QS.parse(queryString, {
      ignoreQueryPrefix: true,
    }) as unknown as StripeCallbackParams
    try {
      if (!query.code) {
        throw new Error(`Error parsing code from query: code=${query.code}`)
      }

      const { code } = query
      const redirectBackTo = link.profileBillingPayoutMethod()
      const connectParams: StripeCallbackHandlerParams = {
        code,
      }

      sessionStorage.setItem(
        StripeCallbackHandlerParamsStorageKey,
        JSON.stringify(connectParams),
      )

      runInAction(() => {
        this.redirectBackUrl = redirectBackTo
      })
    } catch (err) {
      console.error(err)
      throw new Error('Something went wrong connecting your Stripe account')
    }
  })

  @computed
  get currentWorkspace() {
    return this.getCurrentMember().workspace
  }
}

export class StripeConnectCallbackModalViewModel extends ModalViewModel {
  constructor() {
    super()
  }
}

export class StripeConnectCallbackDelegate {
  constructor(
    private getTeam: (teamSlug: string) => Promise<Team | null>,
    private member: Member,
    private billingStore: BillingStore,
    private payoutMethodStore: PayoutMethodStore,
  ) {}

  hasPendingConnection() {
    const pendingParams = sessionStorage.getItem(
      StripeCallbackHandlerParamsStorageKey,
    )

    return pendingParams !== null
  }

  async finishConnection() {
    try {
      const paramsBlob = sessionStorage.getItem(
        StripeCallbackHandlerParamsStorageKey,
      )
      sessionStorage.removeItem(StripeCallbackHandlerParamsStorageKey)
      if (!paramsBlob) {
        throw new Error('Could not find callback params in session storage')
      }
      const params: StripeCallbackHandlerParams = JSON.parse(paramsBlob)
      const { code } = params

      return this.payoutMethodStore.connectStripe(
        this.member.user.publicId,
        code,
      )
    } catch (err) {
      console.error(err)
      throw err
    }
  }
}
